import { useMemo, useState } from "react";
import { Check, ChevronRight, Languages, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { hasPerm } from "../../../lib/perm";
import { api } from "../../../lib/api";
import { useApi } from "../../customer/hooks";
import { PageHead, btnPrimary, input } from "../../../components/common/Page";

// Inline editor for the English (and Greek typo-fix) name of one category
const NameEditor = ({ c, onSaved }) => {
  const { t } = useI18n();
  const [en, setEn] = useState(c.name_en === c.name_el ? "" : c.name_en);
  const [el, setEl] = useState(c.name_el);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/catalogue/categories/${encodeURIComponent(c.code)}`, { name_en: en, name_el: el }); toast.success(t("common.saved")); onSaved(); }
    catch { toast.error(t("common.error")); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2 px-2 pb-2 sm:flex-row" data-testid={`cat-editor-${c.code}`}>
      <input value={el} onChange={(e) => setEl(e.target.value)} className={`${input} h-10 flex-1`} aria-label="EL" data-testid={`cat-el-${c.code}`} />
      <input value={en} onChange={(e) => setEn(e.target.value)} placeholder={t("x.tr.enPh")} className={`${input} h-10 flex-1`} aria-label="EN" data-testid={`cat-en-${c.code}`} />
      <button type="button" disabled={busy} onClick={save} className={`${btnPrimary} h-10 px-3`} data-testid={`cat-save-${c.code}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("common.save")}</button>
    </div>
  );
};

// business unit > group > family, with item counts from the catalogue
const Node = ({ c, kids, counts, depth, render, canEdit, onSaved }) => {
  const { lang } = useI18n();
  const [open, setOpen] = useState(depth === 0);
  const [edit, setEdit] = useState(false);
  const children = kids(c.code);
  const missingEn = c.name_en === c.name_el;
  return (
    <li data-testid={`staff-category-${c.code}`}>
      <div className="flex items-center gap-1 rounded-lg hover:bg-slate-50" style={{ paddingLeft: 8 + depth * 20 }}>
        <button type="button" onClick={() => setOpen(!open)} className="flex min-h-11 min-w-0 flex-1 items-center gap-2 px-2 text-left">
          <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""} ${children.length ? "" : "invisible"}`} />
          <span className={`min-w-0 flex-1 truncate ${depth === 0 ? "font-semibold text-navy" : "text-sm text-slate-700"}`}>{c[`name_${lang}`]}{missingEn && <span className="ml-2 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800" data-testid={`cat-no-en-${c.code}`}>EN?</span>}</span>
          <span className="shrink-0 rounded-full bg-canvas px-2 text-xs tabular-nums text-slate-600" data-testid={`staff-category-count-${c.code}`}>{counts[c.code] || 0}</span>
        </button>
        {canEdit && <button type="button" onClick={() => setEdit((e) => !e)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="edit" data-testid={`cat-edit-${c.code}`}><Pencil className="h-4 w-4" /></button>}
      </div>
      {edit && <NameEditor c={c} onSaved={() => { setEdit(false); onSaved(); }} />}
      {open && children.length > 0 && <ul>{children.map((k) => render(k, depth + 1))}</ul>}
    </li>
  );
};

const AiCategories = ({ onSaved }) => {
  const { t } = useI18n();
  const [props, setProps] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { const { data } = await api.post("/admin/catalogue/categories/translate"); setProps(data.proposals.map((p) => ({ ...p, pick: !!p.name_en }))); } catch { toast.error(t("common.error")); } finally { setBusy(false); } };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/catalogue/categories-bulk", { items: props.filter((p) => p.pick && p.name_en) }); toast.success(t("x.tr.savedN", { n: data.saved })); setProps(null); onSaved(); }
    catch { toast.error(t("common.error")); } finally { setBusy(false); }
  };
  if (!props) return <button type="button" disabled={busy} onClick={run} className={`${btnPrimary} h-10 px-4`} data-testid="cat-ai-translate">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}{t("x.tr.aiCats")}</button>;
  return (
    <section className="space-y-2 rounded-2xl bg-amber-50 p-3" data-testid="cat-proposals">
      <p className="text-xs text-amber-900">{t("x.tr.reviewNote")}</p>
      <ul className="max-h-80 divide-y divide-amber-100 overflow-y-auto">
        {props.map((p, i) => (
          <li key={p.code} className="flex items-center gap-2 py-1.5 text-sm">
            <input type="checkbox" checked={p.pick} onChange={() => setProps((a) => a.map((x, j) => (j === i ? { ...x, pick: !x.pick } : x)))} className="h-4 w-4 accent-navy" data-testid={`cat-pick-${p.code}`} />
            <span className="w-1/2 truncate text-slate-700">{p.name_el}</span>
            <input value={p.name_en} onChange={(e) => setProps((a) => a.map((x, j) => (j === i ? { ...x, name_en: e.target.value } : x)))} className={`${input} h-9 flex-1`} data-testid={`cat-proposal-${p.code}`} />
          </li>
        ))}
      </ul>
      <div className="flex gap-2"><button type="button" disabled={busy} onClick={save} className={`${btnPrimary} h-10 px-4`} data-testid="cat-save-proposals">{t("x.tr.saveProposals", { n: props.filter((p) => p.pick).length })}</button><button type="button" onClick={() => setProps(null)} className="h-10 rounded-full bg-white px-4 text-sm font-semibold text-navy" data-testid="cat-cancel-proposals">{t("common.cancel")}</button></div>
    </section>
  );
};

export default function StaffCategories() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "softone:admin");
  const cats = useApi("categories", "/catalogue/categories");
  const items = useApi("staffItems", "/catalogue/items");
  const counts = useMemo(() => {
    const m = {};
    (items.data || []).forEach((i) => [i.category, i.subcategory, i.family].filter(Boolean).forEach((k) => { m[k] = (m[k] || 0) + 1; }));
    return m;
  }, [items.data]);
  const list = cats.data || [];
  const kids = (p) => list.filter((c) => (c.parent_code || null) === p);
  const render = (c, depth) => <Node key={c.code} c={c} kids={kids} counts={counts} depth={depth} render={render} canEdit={canEdit} onSaved={() => cats.refetch()} />;
  return (
    <div className="max-w-3xl space-y-4" data-testid="staff-categories-page">
      <PageHead title={t("sprod.catTitle")} sub={t("sprod.catSub", { n: list.length })} />
      {canEdit && <AiCategories onSaved={() => cats.refetch()} />}
      <ul className="rounded-2xl bg-white p-2 shadow-sm" data-testid="staff-categories-tree">{kids(null).map((c) => render(c, 0))}</ul>
    </div>
  );
}
